import { ChangeDetectionStrategy, Component, computed, forwardRef, inject, input, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';

import { LanguageService } from '../../../core/i18n/language.service';
import { Icon } from '../icon/icon';
import { BaseControl } from './base-control';

/**
 * Text/search/password field matching the Papercast `Input` design: a 48px
 * sunken shell (surface-1 fill, hairline ring, 8px radius) with the label
 * rendered *below* the field (Open Sans 600 12/16, secondary ink) per the DS
 * `showLabelBelow` slot. While empty the placeholder (or the label used as
 * placeholder) sits in the box. Search fields get a leading magnifier and an
 * X-clear once non-empty; password fields get a visibility toggle icon. A
 * ControlValueAccessor, so it drops into `ngModel` / reactive forms.
 *
 *   <app-text-input [(ngModel)]="clientId" label="Client ID" placeholder="e.g. Stop B" />
 *
 * Help/error text is rendered by the surrounding `app-form-field`, not here.
 */
@Component({
  selector: 'app-text-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon],
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TextInput), multi: true }],
  template: `
    <div class="flex flex-col gap-2">
      <div
        class="shell flex items-center gap-2 rounded-control px-2 focus-within:ring-2 focus-within:ring-focus"
        [class.min-h-12]="size() === 'default'"
        [class.min-h-9]="size() === 'sm'"
        [class.bg-sunken]="!invalid()"
        [class.shadow-hairline]="!invalid()"
        [class.bg-danger-surface]="invalid()"
        [class.ring-1]="invalid()"
        [class.ring-danger]="invalid()"
        [class.opacity-70]="disabled()"
      >
        @if (type() === 'search') {
          <app-icon class="flex-none text-ink-2" name="icon-search" [size]="iconSize()" />
        }
        <input
          [type]="inputType()"
          [value]="value() ?? ''"
          [placeholder]="effectivePlaceholder()"
          [disabled]="disabled()"
          [attr.aria-label]="label() || null"
          [attr.aria-invalid]="invalid() || null"
          [attr.step]="type() === 'time' && withSeconds() ? '1' : null"
          [attr.lang]="type() === 'time' && withSeconds() ? 'en-GB' : null"
          (input)="setValue($any($event.target).value)"
          (blur)="onTouched()"
          class="min-w-0 flex-1 border-none bg-transparent p-0 text-ink-1 outline-none placeholder:text-ink-3"
          [class.text-[16px]]="size() === 'default'"
          [class.leading-6]="size() === 'default'"
          [class.text-[14px]]="size() === 'sm'"
          [class.leading-5]="size() === 'sm'"
        />

        @if (type() === 'search' && hasValue() && !disabled()) {
          <button
            type="button"
            (click)="clear()"
            class="flex flex-none items-center justify-center text-ink-2 hover:text-ink-1"
            [attr.aria-label]="clearLabel()"
          >
            <app-icon name="icon-x" [size]="iconSize()" />
          </button>
        }
        @if (type() === 'password') {
          <button
            type="button"
            (click)="toggleVisibility()"
            class="flex flex-none items-center justify-center text-ink-2 hover:text-ink-1"
            [attr.aria-label]="visibilityLabel()"
          >
            <app-icon [name]="pwVisible() ? 'icon-visibility-off' : 'icon-visibility-on'" [size]="iconSize()" />
          </button>
        }
      </div>

      @if (label()) {
        <span class="px-2 text-caption font-semibold text-ink-2">{{ label() }}</span>
      }
    </div>
  `,
  styles: `
    /* Hover strengthens the hairline ring to the strong overlay (DS hover
       state); suppressed while focused, where the 2px focus ring rules. */
    .shell:hover:not(:focus-within) {
      box-shadow: inset 0 0 0 1px var(--border-strong);
    }
  `,
})
export class TextInput extends BaseControl<string> {
  private readonly transloco = inject(TranslocoService);
  private readonly language = inject(LanguageService);

  readonly type = input<'text' | 'password' | 'email' | 'search' | 'date' | 'time'>('text');
  readonly label = input('');
  readonly placeholder = input('');
  readonly invalid = input(false);
  /** Shell height: 48px default, 36px small (settings rows, toolbars). */
  readonly size = input<'default' | 'sm'>('default');

  protected readonly iconSize = computed(() => (this.size() === 'sm' ? 18 : 24));
  /** `type="time"` only: show seconds and force a 24-hour clock (HH:mm:ss), regardless of locale. */
  readonly withSeconds = input(false);

  /** Local UI state for the password reveal toggle. */
  protected readonly pwVisible = signal(false);

  /** True once the bound value is a non-empty string. */
  protected readonly hasValue = computed(() => (this.value() ?? '') !== '');

  /** Password shows plain text while revealed; search maps to a text input. */
  protected readonly inputType = computed(() => {
    if (this.type() === 'password') return this.pwVisible() ? 'text' : 'password';
    if (this.type() === 'search') return 'text';
    return this.type();
  });

  /** The label doubles as the placeholder when no explicit placeholder is set. */
  protected readonly effectivePlaceholder = computed(() => this.placeholder() || this.label());

  protected readonly visibilityLabel = computed(() => {
    this.language.current();
    return this.transloco.translate(this.pwVisible() ? 'forms.input.hidePassword' : 'forms.input.showPassword');
  });

  protected readonly clearLabel = computed(() => {
    this.language.current();
    return this.transloco.translate('forms.input.clear');
  });

  protected toggleVisibility(): void {
    this.pwVisible.update((v) => !v);
  }

  protected clear(): void {
    this.setValue('');
    this.onTouched();
  }
}
